import { eventChannel } from 'redux-saga';
import {
  actionChannel,
  flush,
  take,
  fork,
  cancel,
  call,
  put,
  delay,
  race,
  select,
} from 'redux-saga/effects';
import io from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import {
  getCurrentDatabaseId,
  getLatestCognitoAccessToken,
  isCognitoAuthentication,
} from '../helpers/auth/cognito';
import * as actions from '../constants/actions.realtime';
import { SOCKET_READY } from '../constants/action-types';
import { removeCookiesFromSocketRequests } from '../libs/socket';
import config from '../../app-config';
import { getToken } from '../httpMiddleware/authorization';
import { getLoggedIn } from '../selectors/user';

import { onLoginChange } from './login';
import { isLocalhost } from '../helpers/environment';

const realTimeActions = Object.values(actions);

const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const TOKEN_EXPIRATION_BUFFER = 5 * 60; // 5 minutes in seconds
const RETRY_DELAY = 3000; // 3 seconds

// Export for testing
export function isTokenExpired(
  token,
  expirationBuffer = TOKEN_EXPIRATION_BUFFER
) {
  if (!token) return true;

  try {
    const payload = jwtDecode(token);

    if (!payload.exp) return true;

    // Check if the token is expired or will expire soon
    // exp is in seconds, Date.now() is in milliseconds
    const currentTime = Date.now() / 1000;
    const expirationTime = payload.exp;
    const timeUntilExpiration = expirationTime - currentTime;
    const isExpired = timeUntilExpiration < expirationBuffer;

    return isExpired;
  } catch (error) {
    return true; // Assume it's expired to be safe
  }
}

// Export for testing
export function* getValidToken(options = {}) {
  const { forceRefresh = false } = options;

  // Check if user is logged in
  const loggedIn = yield select(getLoggedIn);

  if (!loggedIn) {
    return null;
  }

  const currentToken = yield call(getToken);

  if (!currentToken) {
    return null;
  }

  // If token is valid and we're not forcing a refresh, return it
  const isExpired = isTokenExpired(currentToken);

  if (!forceRefresh && !isExpired) {
    return currentToken;
  }

  // Get a fresh token
  const newToken = yield call(getToken, { forceRefresh: true });

  if (!newToken) {
    return null;
  }

  return newToken;
}

// The token to send to the realtime socket. HTTP requests use the id token, but
// the socket backend has not been migrated, so Cognito users keep sending the
// access token; legacy/impersonation keep their own token.
function* getSocketToken(options) {
  const token = yield call(getValidToken, options);

  if (!token) {
    return null;
  }

  return isCognitoAuthentication(token)
    ? yield call(getLatestCognitoAccessToken)
    : token;
}

// Plain-async counterpart of getSocketToken for the socket's own reconnect
// handlers (UNAUTHORIZED / server-disconnect), which run outside the saga and
// cannot `yield`. The realtime backend is not migrated, so Cognito sockets must
// reauth with the ACCESS token — `getToken` now returns the id token (used by
// HTTP/GraphQL), which the socket backend would reject.
async function resolveSocketToken(options) {
  const token = await getToken(options);

  if (!token) {
    return null;
  }

  return isCognitoAuthentication(token)
    ? await getLatestCognitoAccessToken(options?.forceRefresh)
    : token;
}

// Export for testing
export function makeIOChannel(socket) {
  return eventChannel((emit) => {
    socket.on('reconnect', () => {
      socket.emit('subscribe');
    });

    realTimeActions.forEach((event) => {
      // Listen for all real time action types from the socket,
      // and dispatch them, marking them as *from* the socket
      socket.on(event, (data) =>
        emit({
          type: event,
          data,
          __FROM_SOCKET__: true,
        })
      );
    });

    return () => {
      socket.disconnect();
    };
  });
}

// Export for testing
export function* socketListener(socket) {
  const channel = yield call(makeIOChannel, socket);

  while (true) {
    try {
      // Get a valid token before processing actions
      const token = yield call(getValidToken);

      if (!token) {
        // Wait before retrying to get a valid token
        yield delay(TOKEN_CHECK_INTERVAL);
      } else {
        const action = yield take(channel);
        yield put(action);
      }
    } catch (error) {
      // Wait a bit before retrying to avoid tight error loops
      yield delay(1000);
    }
  }
}

// Export for testing
export function* sendEventsToSocket(socket, localChannel) {
  yield take(SOCKET_READY);

  while (true) {
    const action = yield take(localChannel);

    const { type, __FROM_SOCKET__, ...data } = action;

    if (__FROM_SOCKET__ === false) {
      // Get a valid token before sending events
      const latestToken = yield call(getSocketToken);

      if (latestToken) {
        socket.auth = { ...socket.auth, token: latestToken };
        // Update query token for backward compatibility
        if (socket.io?.opts?.query) {
          socket.io.opts.query.token = latestToken;
        }
        yield fork([socket, 'emit'], type, data);
      }
    }
  }
}

// Export for testing
export function* socketEmitter(socket, localChannel) {
  yield call([socket, 'emit'], 'subscribe');
  yield fork(sendEventsToSocket, socket, localChannel);
}

/**
 * Refreshes the socket token periodically or when needed
 */
// Export for testing
export function* refreshToken(socket) {
  while (true) {
    try {
      // Check token every interval
      yield delay(TOKEN_CHECK_INTERVAL);

      const currentToken = socket.auth?.token;

      // Check if token needs refresh
      if (isTokenExpired(currentToken)) {
        // Use race to implement a timeout for token refresh
        const { token, timeout } = yield race({
          token: call(getSocketToken, { forceRefresh: true }),
          timeout: delay(30000), // 30 second timeout
        });

        if (!timeout) {
          if (token) {
            // Update the socket auth
            socket.auth = { ...socket.auth, token };
            // Update query token for backward compatibility
            if (socket.io?.opts?.query) {
              socket.io.opts.query.token = token;
            }

            // Force reconnect to apply new token if connected
            if (socket.connected) {
              socket.disconnect();
              socket.connect();
            }
          } else {
            break;
          }
        }
      }
    } catch (error) {
      // Wait before retrying to avoid tight error loops
      yield delay(RETRY_DELAY);
    }
  }
}

// Module-level storage for socket resources (accessible from ioSaga)
// Store all resources in a single object to avoid race conditions
let currentSocketResources = null;

// Export for testing
export function* createSocket(localChannel) {
  // For SSO/federated auth: token comes from another frontend
  // Cognito needs time to initialize and retrieve the token
  // Retry with exponential backoff to wait for token availability
  let token = null;
  let retries = 0;
  const maxRetries = 20; // Increase to 20 attempts for SSO scenarios
  const initialDelay = 500; // Start with 500ms

  while (!token && retries < maxRetries) {
    token = yield call(getSocketToken);

    if (!token) {
      retries++;

      if (retries < maxRetries) {
        // Exponential backoff: 500ms, 1s, 2s, 4s, 8s, then cap at 8s
        // Total wait time: ~60 seconds for SSO initialization
        const delayTime = Math.min(initialDelay * 2 ** (retries - 1), 8000);
        yield delay(delayTime);
      }
    }
  }

  // If still no token after retries, give up
  if (!token) {
    return null;
  }

  let socketShouldReconnect = true;
  const isCognitoUser = token && isCognitoAuthentication(token);
  // user id derived from the verified id token claim, not from a cookie
  const socketUserId = isCognitoUser
    ? yield call(getCurrentDatabaseId)
    : undefined;

  // Remove cookies if user is cognito and running the repo locally
  if (isCognitoUser && isLocalhost()) {
    removeCookiesFromSocketRequests();
  }

  const socket = yield call(io, config.socketIoUri, {
    auth: {
      token,
    },
    query: {
      // Send token in query for backward compatibility - backend reads from both
      token,
      ...(isCognitoUser && {
        'x-new-auth': true,
        'x-user-id': socketUserId,
      }),
    },
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    // Performance optimizations
    compression: true,
    forceNew: false,
    transports: ['websocket', 'polling'],
  });

  // Store socket immediately to prevent race conditions during cleanup
  // Initialize with socket, tasks will be added as they're created
  currentSocketResources = {
    socket,
    refreshTask: null,
    listenerTask: null,
    emitterTask: null,
  };

  // Start all socket tasks and store them as they're created
  const refreshTask = yield fork(refreshToken, socket);
  currentSocketResources.refreshTask = refreshTask;

  const listenerTask = yield fork(socketListener, socket);
  currentSocketResources.listenerTask = listenerTask;

  const emitterTask = yield fork(socketEmitter, socket, localChannel);
  currentSocketResources.emitterTask = emitterTask;

  yield put({ type: SOCKET_READY });

  // Handle unauthorized errors
  socket.on('UNAUTHORIZED', () => {
    // Try to refresh the token on unauthorized
    (async () => {
      try {
        const newToken = await resolveSocketToken({ forceRefresh: true });

        // Verify the new token is valid
        if (newToken && !isTokenExpired(newToken)) {
          socket.auth = { ...socket.auth, token: newToken };
          // Update query token for backward compatibility
          if (socket.io?.opts?.query) {
            socket.io.opts.query.token = newToken;
          }

          // Force reconnection to send the new token to server
          if (socket.connected) {
            socket.disconnect();
            socket.connect();
          } else {
            socket.connect();
          }
        } else {
          // If we can't get a valid token, stop trying to reconnect
          socketShouldReconnect = false;
        }
      } catch (error) {
        // If we encounter an error refreshing the token, stop trying to reconnect
        // This prevents infinite retry loops but lets the logout happen naturally
        socketShouldReconnect = false;
      }
    })();
  });

  socket.on('disconnect', async (reason) => {
    if (socketShouldReconnect && reason === 'io server disconnect') {
      try {
        const newToken = await resolveSocketToken({ forceRefresh: true });

        // If no token is available, don't try to reconnect
        if (!newToken) {
          return;
        }

        socket.auth = { ...socket.auth, token: newToken };
        // Update query token for backward compatibility
        if (socket.io?.opts?.query) {
          socket.io.opts.query.token = newToken;
        }
        socket.connect();
      } catch (error) {
        // If token refresh fails (e.g., user logged out), don't try to reconnect
        // The error is caught to prevent the logout redirect from socket handlers
        socketShouldReconnect = false;
      }
    }
  });

  // Don't return anything - the resources are stored in module-level variable
  // that will be cleaned up in ioSaga
}

// Helper generator to cleanup socket resources
function* cleanupSocket() {
  if (!currentSocketResources) return;

  const { refreshTask, listenerTask, emitterTask, socket } =
    currentSocketResources;

  // Cancel all forked tasks (handle partial creation if createSocket was cancelled)
  if (refreshTask) {
    yield cancel(refreshTask);
  }
  if (listenerTask) {
    yield cancel(listenerTask);
  }
  if (emitterTask) {
    yield cancel(emitterTask);
  }

  // Disconnect socket (always cleanup socket even if tasks weren't fully created)
  if (socket) {
    // Remove all event listeners to prevent memory leaks
    socket.removeAllListeners();
    if (socket.connected) {
      socket.disconnect();
    }
  }

  // Clear the resources object
  currentSocketResources = null;
}

export default function* ioSaga() {
  let socketTask = null;

  // Listen for all real time action types from redux,
  // and emit them *only if* they are marked explicitly as
  // *to* the socket
  const localChannel = yield actionChannel(realTimeActions);

  // Give the app a moment to hydrate user state from cookies/SSO
  // This delay allows fetchUser() from index.js to complete
  yield delay(500);

  // Check if user is already logged in on app start
  let initialLoggedIn = yield select(getLoggedIn);

  // If still not logged in after delay, wait for SET_USER with timeout
  if (!initialLoggedIn) {
    const { setUser } = yield race({
      setUser: take('platform/users/SET_USER'),
      timeout: delay(2000), // Wait max 2 seconds for initial auth
    });

    if (setUser) {
      // User state was set, check again
      initialLoggedIn = yield select(getLoggedIn);
    }
  }

  if (initialLoggedIn) {
    // User is already logged in (e.g., SSO, page refresh)
    // Create socket immediately
    socketTask = yield fork(createSocket, localChannel);
  }

  while (true) {
    try {
      const loggedIn = yield call(onLoginChange);

      // Cleanup previous socket if exists
      if (socketTask) {
        yield cancel(socketTask);
        yield call(cleanupSocket);
        socketTask = null;
      }

      if (loggedIn) {
        // Create socket (user just logged in)
        socketTask = yield fork(createSocket, localChannel);
      } else {
        // User logged out
        yield flush(localChannel);
      }
    } catch (error) {
      // Wait before retrying to avoid tight error loops
      yield delay(RETRY_DELAY);
    }
  }
}
